import * as React from "react"
import { cn } from "../../lib/utils"

export interface RadioGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
}

export interface RadioItemProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  description?: string;
  value: string;
}

const RadioItem = React.forwardRef<HTMLInputElement, RadioItemProps>(
  ({ className, label, description, id, checked, onChange, value, ...props }, ref) => {
    return (
      <div className="flex items-start gap-2">
        <input
          type="radio"
          ref={ref}
          id={id}
          value={value}
          checked={checked}
          onChange={onChange}
          className={cn(
            "mt-1 h-4 w-4 rounded-full border border-gray-300 text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2",
            className
          )}
          {...props}
        />
        {(label || description) && (
          <div className="flex flex-col">
            {label && (
              <label 
                htmlFor={id}
                className="text-sm font-medium text-gray-700 cursor-pointer"
              >
                {label}
              </label>
            )}
            {description && (
              <span className="text-sm text-gray-500">
                {description}
              </span>
            )}
          </div>
        )}
      </div>
    )
  }
)
RadioItem.displayName = "RadioItem"

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ className, value, onValueChange, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex gap-4", className)}
        role="radiogroup"
        {...props}
      >
        {React.Children.map(children, child => {
          if (React.isValidElement<RadioItemProps>(child)) {
            return React.cloneElement(child, {
              ...child.props,
              checked: child.props.value === value,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.checked) {
                  onValueChange?.(child.props.value);
                }
              },
            });
          }
          return child;
        })}
      </div>
    )
  }
)
RadioGroup.displayName = "RadioGroup"

export { RadioGroup, RadioItem }